import type { HydraMarket } from '@/clients/HydraClient';
import type { Venue } from '@/views/ValueDashboard/types';

export interface GolfMatchIdentity {
  key: string;
  venue: Venue;
  ticker: string;
  title: string;
  proposition: string;
  startTimeMs: number;
  date: string;
  /** Full source identity, not quote state. Changes invalidate a draft. */
  identity: string;
  groupIdentity: string;
  outcome: string;
}

/** Dated match winners only. No ticker/name splitting, home/away inference,
 * cross-venue pairing, or conversion of one participant's YES into another's
 * NO. The existing producer fields must positively identify the match. */
export function golfMatchIdentity(m: HydraMarket): GolfMatchIdentity | null {
  if (m.sport?.toLowerCase() !== 'golf'
    || !['game', 'moneyline'].includes(m.marketType ?? '')
    || m.class !== 'moneyline' || (m.period != null && m.period !== 'full')
    || m.dhAmbiguous || !m.eventTicker?.trim() || !m.eventTitle?.trim()
    || !m.title?.trim() || !m.ticker?.trim()
    || !['kalshi', 'polymarket'].includes(m.source)) return null;
  // A condition is a binary market, not the enclosing match. Older Poly
  // futures use conditionId as eventTicker and must not become matches.
  if (m.source === 'polymarket' && m.eventTicker === m.conditionId) return null;
  const pair = m.canonicalTeams;
  if (!Array.isArray(pair) || pair.length !== 2
    || pair.some((p) => typeof p !== 'string' || !p.trim() || p.includes('|'))
    || pair[0] === pair[1]) return null;
  const key = m.canonicalGameKeyV2;
  const parts = key?.split('|');
  if (!parts || parts.length !== 5 || parts[0] !== 'golf'
    || !/^\d{4}-\d{2}-\d{2}$/.test(parts[1]) || !/^[1-9]\d*$/.test(parts[2])
    || parts[3] === parts[4] || !pair.includes(parts[3]) || !pair.includes(parts[4])) return null;
  const day = Date.parse(`${parts[1]}T00:00:00Z`);
  if (!Number.isFinite(day) || new Date(day).toISOString().slice(0, 10) !== parts[1]) return null;
  const outcome = m.yesOutcome;
  if (!outcome || (!pair.includes(outcome) && outcome !== 'DRAW')) return null;
  if (m.canonicalYesTeam != null && m.canonicalYesTeam !== outcome) return null;
  const startTimeMs = Date.parse(m.eventStartTime ?? '');
  if (!Number.isFinite(startTimeMs)) return null;
  return {
    // Source event identity keeps rounds/sessions and venue settlement apart,
    // even if a producer accidentally reuses the same canonical pair key.
    key: JSON.stringify(['golf-match', m.source, m.eventTicker]),
    venue: m.source, ticker: m.ticker, title: m.eventTitle,
    proposition: m.title, startTimeMs, date: parts[1],
    outcome,
    groupIdentity: JSON.stringify([key, [...pair].sort(), m.eventTitle, m.eventStartTime]),
    identity: JSON.stringify([m.source, m.eventTicker, key, pair, outcome,
      m.eventTitle, m.title, m.eventStartTime]),
  };
}
